import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { RemoveParentControlsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const mapTrpcResponseToGraphQL = (
  affectedRows: number
): RemoveParentControlsMutation => {
  return {
    delete_control_parent: {
      __typename: 'control_parent_mutation_response',
      affected_rows: affectedRows,
    },
  };
};

export const useDeleteParentControlsTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.control.deleteParents.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    deleteParentControls: async (
      parentId: string,
      controlIds: string[]
    ): Promise<RemoveParentControlsMutation> => {
      const result = await mutation.mutateAsync({
        ParentId: parentId,
        ControlIds: controlIds,
      });

      return mapTrpcResponseToGraphQL(result.affected_rows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
